import React from 'react';
import useSWR from 'swr';
import { useTranslation } from '../../../contexts/translation';
import { File as FileType } from '../../../types/file.types';
import { Hyperlink } from '../../../types/hyperlink.types';
import { API_URL, fetcher } from '../../../utils/apiUrl';

interface Props {
  assetId: number;
}

/**
 * Otevře soubor v novém okně. Náhledovatelné typy (obrázek, pdf, text, video, audio) se otevřou přímo,
 * ostatní typy se rovnou stáhnou.
 */
const handleFileOpen = async (
  id: number,
  name: string,
  contentType: string,
  entityName: 'Asset' | 'Task',
) => {
  const response = await fetch(`${API_URL.FILE_ITEM}?entity=${entityName}&id=${id}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  const blob = await response.blob();
  const fileUrl = URL.createObjectURL(blob);

  if (/^(image\/|application\/pdf|text\/|video\/|audio\/)/.test(contentType)) {
    window.open(fileUrl, '_blank');
  } else {
    const anchor = document.createElement('a');
    anchor.href = fileUrl;
    anchor.download = name;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }
};

const handleHyperlinkOpen = (uri: string) => {
  const href = uri.startsWith('http://') || uri.startsWith('https://') ? uri : `https://${uri}`;
  window.open(href, '_blank');
};

/**
 * Read-only výpis souborů a hyperlinků přiřazených k assetu i jeho taskům. Kliknutím na řádek
 * se dokument otevře v novém okně.
 */
export const AssetAttachmentsList = ({ assetId }: Props) => {
  const { translate } = useTranslation();
  const transFormName = 'File';

  const { data: files } = useSWR<FileType[]>(
    `${API_URL.FILE_LIST}?entity=Asset&entityId=${assetId}`,
    fetcher,
  );
  const { data: hyperlinks } = useSWR<Hyperlink[]>(
    `${API_URL.HYPERLINK_LIST_FILTERED}?entity=Asset&entityId=${assetId}`,
    fetcher,
  );
  // přílohy a hyperlinky tasků navázaných na tento asset
  const { data: taskFiles } = useSWR<FileType[]>(
    `${API_URL.FILE_LIST_BY_ASSET}?assetId=${assetId}`,
    fetcher,
  );
  const { data: taskHyperlinks } = useSWR<Hyperlink[]>(
    `${API_URL.HYPERLINK_LIST_BY_ASSET}?assetId=${assetId}`,
    fetcher,
  );

  // vlastní soubory/hyperlinky assetu mají entity 'Asset', ty z tasků 'Task' (kvůli FILE_ITEM endpointu)
  const allFiles = [
    ...(files ?? []).map((file) => ({ ...file, entityName: 'Asset' as const })),
    ...(taskFiles ?? []).map((file) => ({ ...file, entityName: 'Task' as const })),
  ];
  const allHyperlinks = [...(hyperlinks ?? []), ...(taskHyperlinks ?? [])];

  const isEmpty = allFiles.length === 0 && allHyperlinks.length === 0;

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={{ textAlign: 'left', padding: '6px', borderBottom: '1px solid #ddd' }}>
            {translate!('Name', transFormName)}
          </th>
          <th style={{ textAlign: 'left', padding: '6px', borderBottom: '1px solid #ddd' }}>
            {translate!('Description', transFormName)}
          </th>
        </tr>
      </thead>
      <tbody>
        {allFiles.map((file) => (
          <tr
            key={`file_${file.id}`}
            style={{ cursor: 'pointer' }}
            onClick={() => handleFileOpen(file.id, file.name, file.contentType, file.entityName)}
          >
            <td style={{ padding: '6px', borderBottom: '1px solid #eee' }}>
              <i className="dx-icon-file" style={{ marginRight: '6px' }} />
              {file.name}
            </td>
            <td style={{ padding: '6px', borderBottom: '1px solid #eee' }}>{file.description}</td>
          </tr>
        ))}
        {allHyperlinks.map((hyperlink) => (
          <tr
            key={`hyperlink_${hyperlink.id}`}
            style={{ cursor: 'pointer' }}
            onClick={() => handleHyperlinkOpen(hyperlink.uri)}
          >
            <td style={{ padding: '6px', borderBottom: '1px solid #eee' }}>
              <i className="dx-icon-link" style={{ marginRight: '6px' }} />
              {hyperlink.name}
            </td>
            <td style={{ padding: '6px', borderBottom: '1px solid #eee' }}>{hyperlink.uri}</td>
          </tr>
        ))}
        {isEmpty && (
          <tr>
            <td colSpan={2} style={{ padding: '6px', textAlign: 'center' }}>
              {translate!('No attachments', transFormName)}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};
